import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { FlyerType } from "../../api/campaigns";
import "./BulkDeliveryConfirmationDialog.css";

export interface BulkDeliveryConfirmationDialogProps {
    roadSegmentId: number;
    roadName?: string | null;
    estimatedAddressCount: number;
    bufferM: number;
    flyerTypes: FlyerType[];
    selectedFlyerTypeIds: string[];
    onConfirm: (bufferM: number, flyerTypeIds: string[]) => void;
    onCancel: () => void;
    isLoading?: boolean;
}

export const BulkDeliveryConfirmationDialog = ({
    roadName,
    estimatedAddressCount,
    bufferM: initialBufferM,
    flyerTypes,
    selectedFlyerTypeIds,
    onConfirm,
    onCancel,
    isLoading = false
}: BulkDeliveryConfirmationDialogProps): JSX.Element => {
    const { t } = useTranslation();
    const [bufferM, setBufferM] = useState(initialBufferM || 40.0);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [selectedFlyerIds, setSelectedFlyerIds] = useState<string[]>(selectedFlyerTypeIds);
    const mouseDownOnOverlay = useRef(false);

    const handleConfirm = () => {
        if (selectedFlyerIds.length === 0) {
            // TODO: Show error - need to select at least one flyer type
            return;
        }
        onConfirm(bufferM, selectedFlyerIds);
    };

    const toggleFlyerType = (flyerId: string) => {
        setSelectedFlyerIds((prev) =>
            prev.includes(flyerId)
                ? prev.filter((id) => id !== flyerId)
                : [...prev, flyerId]
        );
    };

    return (
        <div
            className="bulk-delivery-dialog__overlay"
            onMouseDown={(ev) => { mouseDownOnOverlay.current = ev.target === ev.currentTarget; }}
            onMouseUp={(ev) => {
                if (mouseDownOnOverlay.current && ev.target === ev.currentTarget) onCancel();
                mouseDownOnOverlay.current = false;
            }}
        >
            <div
                className="bulk-delivery-dialog__content"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-labelledby="bulk-delivery-dialog__title"
                aria-modal="true"
            >
                <header className="bulk-delivery-dialog__header">
                    <h2 id="bulk-delivery-dialog__title" className="bulk-delivery-dialog__title">
                        {t("map.bulkDelivery.confirmTitle")}
                    </h2>
                </header>

                <div className="bulk-delivery-dialog__body">
                    <p className="bulk-delivery-dialog__message">
                        {roadName
                            ? t("map.bulkDelivery.confirmMessageWithName", {
                                roadName,
                                count: estimatedAddressCount
                            })
                            : t("map.bulkDelivery.confirmMessage", {
                                count: estimatedAddressCount
                            })}
                    </p>

                    {flyerTypes.length > 0 && (
                        <div className="bulk-delivery-dialog__flyer-types">
                            <label className="bulk-delivery-dialog__label">
                                {t("map.bulkDelivery.flyerTypes")}:
                            </label>
                            <div className="bulk-delivery-dialog__flyer-list">
                                {flyerTypes.map((flyer) => (
                                    <label
                                        key={flyer.id}
                                        className="bulk-delivery-dialog__flyer-item"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedFlyerIds.includes(flyer.id)}
                                            onChange={() => toggleFlyerType(flyer.id)}
                                            disabled={isLoading}
                                        />
                                        <span>{flyer.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="bulk-delivery-dialog__advanced">
                        <button
                            type="button"
                            className="bulk-delivery-dialog__advanced-toggle"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            disabled={isLoading}
                        >
                            {showAdvanced ? "▼" : "▶"} {t("map.bulkDelivery.advancedOptions")}
                        </button>
                        {showAdvanced && (
                            <div className="bulk-delivery-dialog__advanced-content">
                                <label className="bulk-delivery-dialog__label">
                                    {t("map.bulkDelivery.bufferDistance")} (m):
                                    <input
                                        type="number"
                                        min="5"
                                        max="50"
                                        step="1"
                                        value={bufferM}
                                        onChange={(e) => setBufferM(Math.max(5, Math.min(50, parseFloat(e.target.value) || 30)))}
                                        disabled={isLoading}
                                        className="bulk-delivery-dialog__buffer-input"
                                    />
                                </label>
                                <p className="bulk-delivery-dialog__buffer-help">
                                    {t("map.bulkDelivery.bufferHelp", { bufferM })}
                                </p>
                            </div>
                        )}
                    </div>

                    <p className="bulk-delivery-dialog__warning">
                        {t("map.bulkDelivery.warning")}
                    </p>
                </div>

                <footer className="bulk-delivery-dialog__footer">
                    <button
                        type="button"
                        className="bulk-delivery-dialog__button bulk-delivery-dialog__button--cancel"
                        onClick={onCancel}
                        disabled={isLoading}
                    >
                        {t("common.cancel")}
                    </button>
                    <button
                        type="button"
                        className="bulk-delivery-dialog__button bulk-delivery-dialog__button--confirm"
                        onClick={handleConfirm}
                        disabled={isLoading || selectedFlyerIds.length === 0}
                    >
                        {isLoading ? t("common.loading") : t("map.bulkDelivery.confirm")}
                    </button>
                </footer>
            </div>
        </div>
    );
};